/**
 * Javascript % operator is a *remainder* operator
 * This method provides a *modulo* operator where negative numbers
 * go back around to the top of the range
 * @param num1 The input number
 * @param num2 The modulo number
 */
export function mod(num1: number, num2: number): number {
  return ((num1 % num2) + num2) % num2
}

export function adjustForDecimal(num: number): number {
  return Math.round(num * 100) / 100
}

/**
 * Clamps number within the inclusive lower and upper bounds.
 */
export function clamp(
  min: number,
  value: number,
  max: number,
): number {
  return Math.min(Math.max(value, min), max)
}

export function isNumber(value: any): value is number {
  return typeof value === 'number' && isFinite(value)
}

export function safeDivideAndCeil(value: number, divisor: number): number
export function safeDivideAndCeil(value: number | undefined, divisor: number): number | undefined
export function safeDivideAndCeil(value: number | undefined, divisor: number) {
  if (value === undefined) return
  if (divisor <= 0) return value
  if (value <= 0) return value
  return Math.ceil(value / divisor)
}

export function randomIntegerBetween(
  /** inclusive */
  min: number,
  /** inclusive */
  max: number,
): number {
  const minCeiled = Math.ceil(min)
  const maxFloored = Math.floor(max)

  return Math.floor(Math.random() * (maxFloored - minCeiled + 1) + minCeiled)
}

export function floatify(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100
}

/**
 * Compares two money amounts at cent precision, ignoring floating-point noise.
 *
 * Both operands are rounded to cents (via `floatify`) before comparing, so
 * values that are equal to the cent — e.g. `723.6800000000001` and `723.68` —
 * compare as equal (return `0`) instead of the binary noise flipping the result.
 *
 * @returns `-1` when `a < b`, `1` when `a > b`, `0` when equal to the cent.
 */
export function compareMoney(a: number, b: number): -1 | 0 | 1 {
  const roundedA = floatify(a)
  const roundedB = floatify(b)
  if (roundedA < roundedB) return -1
  if (roundedA > roundedB) return 1
  return 0
}

const ORDINAL_WORDS = ['', 'first', 'second', 'third', 'fourth', 'fifth', 'sixth', 'seventh', 'eighth', 'ninth', 'tenth']

/**
 * Returns the ordinal form of `n`.
 * 1–10 are spelled out ("first" … "tenth"); 11+ use a numeric suffix ("11th", "21st").
 *
 * @example
 *   toOrdinal(3)   // → 'third'
 *   toOrdinal(11)  // → '11th'
 *   toOrdinal(21)  // → '21st'
 *   toOrdinal(112) // → '112th'  (11–13 in any hundred always take 'th')
 */
export function toOrdinal(n: number): string {
  if (n >= 1 && n <= 10) return ORDINAL_WORDS[n]

  const mod100 = n % 100
  if (mod100 >= 11 && mod100 <= 13) return `${n}th`

  switch (n % 10) {
    case 1: return `${n}st`
    case 2: return `${n}nd`
    case 3: return `${n}rd`
    default: return `${n}th`
  }
}
